import { useCallback, useEffect, useState } from 'react';
import { uploadFile, type UploadResult } from '../api/storage';

interface Props {
  onUploaded: (result: UploadResult | null) => void;
  disabled?: boolean;
}

export function ScreenshotDrop({ onUploaded, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState<UploadResult | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (disabled || uploading) return;
      setError('');
      setUploading(true);
      try {
        const r = await uploadFile(file);
        setUploaded(r);
        onUploaded(r);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen');
        setUploaded(null);
        onUploaded(null);
      } finally {
        setUploading(false);
      }
    },
    [disabled, uploading, onUploaded]
  );

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (disabled || uploading) return;
      const file = e.clipboardData?.files?.[0];
      if (file && file.type.startsWith('image/')) {
        e.preventDefault();
        void handleFile(file);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [handleFile, disabled, uploading]);

  function clear() {
    setUploaded(null);
    onUploaded(null);
  }

  const cls = `dropzone ${dragOver ? 'over' : ''} ${uploaded ? 'has-file' : ''}`;

  return (
    <div
      className={cls}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) void handleFile(file);
      }}
    >
      {uploading && (<><span className="spinner" /> &nbsp; lade hoch…</>)}
      {!uploading && uploaded && (
        <span>
          📎 {uploaded.original_filename} (storage/{uploaded.id})
          {' '}
          <button
            type="button"
            onClick={clear}
            style={{ padding: '0 0.4rem', fontSize: '0.75rem', marginLeft: '0.4rem' }}
          >
            ✕
          </button>
        </span>
      )}
      {!uploading && !uploaded && (
        <span>📎 Screenshot droppen oder ⌘/Ctrl+V einfügen</span>
      )}
      {error && (
        <div className="error" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
